import { useEffect, useRef } from 'react';
import { useProjectStore, useTargetId } from '../store/projectStore';
import { useMainContentTabs, useLayoutStore } from '../store/layoutStore';

/**
 * Hook to manage the welcome tab's lifecycle:
 * - Opens welcome tab on startup if no company or application is selected
 * - Reopens welcome tab if company/application selection is cleared
 * - Handles tenant switch by checking state after rehydration
 */
export function useWelcomeTabManager() {
  const companyId = useProjectStore((state) => state.companyId);
  const targetId = useTargetId();
  const { tabs, openTab, setActiveTab } = useMainContentTabs();
  const hasInitialized = useRef(false);

  const hasWelcomeTab = tabs.some((tab) => tab.id === 'welcome');
  const needsWelcome = !companyId || !targetId;

  // Subscribe to rehydration to handle tenant switches
  useEffect(() => {
    const unsubscribe = useLayoutStore.persist.onFinishHydration(() => {
      // After rehydration, check if we need to show welcome
      const { companyId: rehydratedCompanyId } = useProjectStore.getState();
      const { mainContentTabs } = useLayoutStore.getState();
      const hasWelcome = mainContentTabs.some((tab) => tab.id === 'welcome');

      if (!rehydratedCompanyId && !hasWelcome) {
        useLayoutStore.getState().openMainContentTab({
          id: 'welcome',
          title: 'Welcome',
          type: 'welcome',
        });
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    // Skip initial render - let rehydration handler take care of it
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      // But still open welcome on first render if needed and no tabs exist
      if (needsWelcome && tabs.length === 0) {
        openTab({
          id: 'welcome',
          title: 'Welcome',
          type: 'welcome',
        });
      }
      return;
    }

    // If no company/application is selected and no welcome tab exists, open it
    if (needsWelcome && !hasWelcomeTab) {
      openTab({
        id: 'welcome',
        title: 'Welcome',
        type: 'welcome',
      });
    }

    // If welcome tab exists and we need it, make sure it's active
    if (needsWelcome && hasWelcomeTab) {
      setActiveTab('welcome');
    }
  }, [
    companyId,
    targetId,
    hasWelcomeTab,
    needsWelcome,
    openTab,
    setActiveTab,
    tabs.length,
  ]);

  return {
    shouldShowWelcomeTab: needsWelcome,
    hasWelcomeTab,
  };
}
